import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Zap, Globe, Sparkles, TrendingUp, Trophy, ImageOff, Check, Star } from "lucide-react";
import FaceScanner from "../components/FaceScanner";
import Reveal from "../components/Reveal";
import Faq from "../components/Faq";
import CtaSection from "../components/CtaSection";
import { PLANS } from "../data/plans";
import { BRAND } from "../lib/brand";
import api from "../lib/api";
import { useSeo, orgLd, websiteLd, softwareLd, faqLd } from "../lib/seo";

const HERO_BG = "https://images.unsplash.com/photo-1714548529197-537c1f0b6aa7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMHRlY2huaWNhbCUyMHNjYW5uaW5nJTIwbWVzaHxlbnwwfHx8fDE3ODIyNDcyNjF8MA&ixlib=rb-4.1.0&q=85";
const AVATARS = [
  "https://images.unsplash.com/photo-1709004915865-38bc70f4cb78?crop=entropy&cs=srgb&fm=jpg&w=120&q=80&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxmYXNoaW9uJTIwbWFsZSUyMG1vZGVsfGVufDB8fHx8MTc4MjI0NzI2MXww&ixlib=rb-4.1.0",
  "https://images.unsplash.com/photo-1654765437547-6b572f52ee1a?crop=entropy&cs=srgb&fm=jpg&w=120&q=80&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxmYXNoaW9uJTIwZmVtYWxlJTIwbW9kZWx8ZW58MHx8fHwxNzgyMjQ3MjYxfDA&ixlib=rb-4.1.0",
  "https://images.unsplash.com/photo-1610608077449-9dd751e8b988?crop=entropy&cs=srgb&fm=jpg&w=120&q=80&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxmYXNoaW9uJTIwZmVtYWxlJTIwbW9kZWx8ZW58MHx8fHwxNzgyMjQ3MjYxfDA&ixlib=rb-4.1.0",
];

const FEATURES = [
  { icon: Globe, title: "Works on any device", desc: "No App Store download, no iPhone TrueDepth needed. Just open your browser and scan." },
  { icon: Zap, title: "Instant 8-metric analysis", desc: "Symmetry, jawline, golden ratio, canthal tilt, thirds, harmony, cheekbones and skin — in seconds." },
  { icon: Sparkles, title: "AI Coach with a real plan", desc: "Not vague tips. A structured multi-week routine tailored to your face's biggest opportunities." },
  { icon: TrendingUp, title: "Future-Face projection", desc: "See your realistic ceiling and exactly how to reach it. Track progress as you improve." },
  { icon: Trophy, title: "Leaderboard & Face Battles", desc: "Compare with others, climb the global ranking, and challenge friends head-to-head." },
  { icon: ImageOff, title: "Viral share pages", desc: "Every result gets its own branded, shareable page with an auto-generated image card." },
];

const FAQS = [
  { q: "Do I need to download an app?", a: "No. Aesthetiq runs entirely in your browser on any device — iPhone, Android, laptop or tablet. Most competitors are iOS-only apps that require a TrueDepth sensor." },
  { q: "Is it really free?", a: "Yes. You get a full 8-metric analysis, an AI Coach summary and a shareable FaceCard for free, with 5 scans a month. Paid plans unlock progress tracking, the full coach plan and higher limits." },
  { q: "How accurate is the analysis?", a: "Our vision-AI engine estimates facial geometry — symmetry, ratios, thirds and tilt — from a clear front-facing photo. It's designed for consistent, repeatable tracking rather than a clinical diagnosis." },
  { q: "Is my photo private?", a: "You control whether each FaceCard is public or private. Private scans are never shown on the leaderboard or shared." },
  { q: "Can I track my progress over time?", a: "Yes — create a free account to save your history, and upgrade to Pro for unlimited progress charts and Future-Face projection." },
];

export default function Home() {
  const [stats, setStats] = useState({ total_scans: 128400 });
  useEffect(() => { api.get("/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);

  useSeo({
    title: null,
    description: `${BRAND.short} Get your facial symmetry, jawline, golden ratio and skin scores plus an AI improvement plan — free, in your browser.`,
    path: "/",
    jsonLd: [orgLd, websiteLd, softwareLd, faqLd(FAQS)],
  });

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_BG} alt="" className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/90 to-background" />
        </div>
        <div className="container grid items-center gap-12 py-16 lg:grid-cols-2 lg:py-24">
          <div>
            <motion.span
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-success" /> No download · Works on any device
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="mt-5 font-display text-5xl leading-[0.95] text-balance sm:text-6xl lg:text-7xl"
            >
              Decode your face with <span className="text-primary">real AI scores.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
              className="mt-5 max-w-md text-lg text-muted-foreground"
            >
              Upload a selfie and get an 8-metric facial analysis, your potential score, and a personalised AI plan to improve — instantly, in your browser.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
              className="mt-8 flex items-center gap-4"
            >
              <div className="flex -space-x-2">
                {AVATARS.map((a, i) => (
                  <img key={i} src={a} alt="" className="h-9 w-9 rounded-full border-2 border-background object-cover" />
                ))}
              </div>
              <div className="text-sm">
                <div className="flex items-center gap-1 text-primary">
                  {[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-current" />)}
                </div>
                <p className="text-muted-foreground">{Math.round(stats.total_scans / 1000)}k+ faces analysed</p>
              </div>
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }}>
            <FaceScanner />
          </motion.div>
        </div>
      </section>

      {/* SOCIAL PROOF STRIP */}
      <section className="border-b border-border bg-card/40">
        <div className="container flex flex-wrap items-center justify-center gap-x-10 gap-y-3 py-6 text-center text-sm text-muted-foreground">
          <span className="font-mono">{(stats.total_scans).toLocaleString()}+ faces scanned</span>
          <span className="hidden h-4 w-px bg-border sm:block" />
          <span className="font-mono">1,000+ data points / scan</span>
          <span className="hidden h-4 w-px bg-border sm:block" />
          <span className="font-mono">8 facial metrics</span>
          <span className="hidden h-4 w-px bg-border sm:block" />
          <span className="font-mono">Free to start</span>
        </div>
      </section>

      {/* FEATURES */}
      <section className="container py-20">
        <Reveal>
          <span className="label-eyebrow">Why Aesthetiq</span>
          <h2 className="mt-3 max-w-2xl font-display text-4xl text-balance sm:text-5xl">Everything the iPhone-only apps do — plus the things they can't.</h2>
        </Reveal>
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.05}>
              <div className="group h-full rounded-xl border border-border bg-card p-6 transition hover:border-primary/40">
                <f.icon className="h-7 w-7 text-primary" />
                <h3 className="mt-4 font-display text-xl">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* COMPARISON HIGHLIGHT */}
      <section className="border-y border-border bg-card/40">
        <div className="container py-20">
          <Reveal>
            <div className="grid items-center gap-10 lg:grid-cols-2">
              <div>
                <span className="label-eyebrow">The honest difference</span>
                <h2 className="mt-3 font-display text-4xl text-balance">Built for the open web, not locked in an app store.</h2>
                <p className="mt-4 text-muted-foreground">
                  Tools like FaceKit need an iPhone with a TrueDepth sensor and a weekly subscription before you see a single result. Aesthetiq gives you the full analysis free, on any device, with a result page you can actually share.
                </p>
                <Link to="/vs" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary" data-testid="home-compare-link">
                  Compare all 12 alternatives <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="overflow-hidden rounded-xl border border-border">
                {[
                  ["Works without an iPhone", true],
                  ["No app download required", true],
                  ["Full result free, no trial trap", true],
                  ["Public shareable result page", true],
                  ["AI Coach plan + projection", true],
                  ["Global leaderboard on the web", true],
                ].map(([label, ok], i) => (
                  <div key={i} className="flex items-center justify-between border-b border-border bg-card px-5 py-3.5 last:border-0">
                    <span className="text-sm">{label}</span>
                    <Check className="h-4 w-4 text-success" />
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="container py-20">
        <Reveal>
          <div className="text-center">
            <span className="label-eyebrow">Pricing</span>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl">Start free. Upgrade when you're serious.</h2>
          </div>
        </Reveal>
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {PLANS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.05}>
              <div className={`relative h-full rounded-xl border bg-card p-7 ${p.popular ? "border-primary" : "border-border"}`}>
                {p.popular && <span className="absolute -top-3 left-7 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">Most popular</span>}
                <h3 className="font-display text-2xl">{p.name}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{p.tagline}</p>
                <p className="mt-5 font-display text-4xl">${p.monthly}<span className="text-base text-muted-foreground">/mo</span></p>
                <p className="mt-1 text-xs text-muted-foreground">{p.limitLabel}</p>
                <Link to="/pricing" className={`mt-6 block rounded-md py-2.5 text-center text-sm font-semibold transition ${p.popular ? "bg-primary text-primary-foreground hover:brightness-110" : "border border-border hover:border-primary/50"}`}>
                  {p.cta}
                </Link>
                <ul className="mt-6 space-y-2.5">
                  {p.features.slice(0, 5).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{f}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="container max-w-3xl py-20">
        <Reveal>
          <h2 className="text-center font-display text-4xl sm:text-5xl">Questions, answered.</h2>
          <div className="mt-10"><Faq faqs={FAQS} testId="home-faq" /></div>
        </Reveal>
      </section>

      <CtaSection />
    </>
  );
}
